import { ProjectPriorityResponse, ProjectPriorityRequest } from './../../entities/project/ProjectPriorityResponse';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { ProjectPriorityAPI } from '@business/api/projectPriority';

export class ProjectPriorityBloc extends BaseBloc {
  private readonly projectPriorityRepo = new Repository<ProjectPriorityResponse>({
    getItemId: priority => priority.id,
  });

  readonly priorities$ = this.projectPriorityRepo.items$;
  readonly selectProjectPriority = this.projectPriorityRepo.selectItem;

  onReset() {
    this.projectPriorityRepo.reset();
  }

  fetchProjectPriorities = () => {
    return ProjectPriorityAPI.fetchProjectPriorities().pipe(
      this.projectPriorityRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_project_priority_error') }),
    );
  };

  fetchProjectPriority = (id: string) => {
    return ProjectPriorityAPI.fetchProjectPriority(id).pipe(
      this.projectPriorityRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_project_priority_error') }),
    );
  };

  createProjectPriority = (stage: ProjectPriorityRequest) => {
    return ProjectPriorityAPI.createProjectPriority(stage).pipe(
      this.projectPriorityRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_project_priority'),
      }),
    );
  };

  updateProjectPriority = (id: string, stage: ProjectPriorityRequest) => {
    return ProjectPriorityAPI.updateProjectPriority(id, stage).pipe(
      this.projectPriorityRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project_priority'),
      }),
    );
  };

  deleteProjectPriority = (id: string) => {
    return showDeleteConfirmation('Delete Priority', 'Do you really want to remove this priority?').pipe(
      switchMap(() => ProjectPriorityAPI.deleteProjectPriority(id)),
      this.projectPriorityRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_project_priority'),
      }),
    );
  };

  defaultProjectPriority = (id: string) => {
    return ProjectPriorityAPI.defaultProjectPriority(id).pipe(
      this.projectPriorityRepo.ops.reset(),
      handleMessage({
        type: requestMessage('default_project_priority'),
      }),
    );
  };

  removeDefaultProjectPriority = (id: string) => {
    return ProjectPriorityAPI.removeDefaultProjectPriority(id).pipe(
      this.projectPriorityRepo.ops.reset(),
      handleMessage({
        type: requestMessage('remove_default_project_priority'),
      }),
    );
  };
}
